import { QueryParams } from '@Activity/common/constants';
import { getFragmentData } from '@api/gql';
import { SyslogEventsFilterInput } from '@api/gql/graphql';
import { usePagePagination } from '@common/hooks/use-page-pagination';
import { Loader } from '@components';
import TablePagination from '@components/TablePagination';
import { Stack } from '@mui/material';
import SystemLogTable from '@SystemLog/components/SystemLogTable';
import { syslogEventFragmentDocument } from '@SystemLog/services/gql';
import { useGetSyslogEvents } from '@SystemLog/services/systemLogQueries';
import { getStartEndDateFromQP } from '@utils';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ACTION_FILTER_PARAM,
  ACTOR_FILTER_PARAM,
  SOURCE_FILTER_PARAM,
  TARGET_TYPE_FILTER_PARAM,
  TARGET_FILTER_PARAM,
} from './SystemLogFilters';

export const EVENT_ID_PARAM = 'eventId';

export default function SystemLogEvents() {
  const { offset, pageSize, handlePageChange } = usePagePagination();
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = useMemo(() => {
    const filterInput: SyslogEventsFilterInput = {};

    const actions = searchParams.getAll(ACTION_FILTER_PARAM);
    if (actions.length) filterInput.actions = actions;

    const sources = searchParams.getAll(SOURCE_FILTER_PARAM);
    if (sources.length) filterInput.sources = sources;

    const actors = searchParams.getAll(ACTOR_FILTER_PARAM);
    if (actors.length) filterInput.actors = actors;

    const targetTypes = searchParams.getAll(TARGET_TYPE_FILTER_PARAM);
    if (targetTypes.length) filterInput.targetTypes = targetTypes;

    const targets = searchParams.getAll(TARGET_FILTER_PARAM);
    if (targets.length) filterInput.targets = targets;

    const search = searchParams.get(QueryParams.search);
    if (search) filterInput.search = search;

    const { startDate, endDate } = getStartEndDateFromQP(searchParams);
    if (startDate || endDate) {
      filterInput.timestamp = {};
      if (startDate) filterInput.timestamp.after = startDate.fullIsoString;
      if (endDate) filterInput.timestamp.before = endDate.fullIsoString;
    }

    return filterInput;
  }, [searchParams]);

  const { data, isFetching, isFetched } = useGetSyslogEvents({
    offset,
    take: pageSize,
    filter,
  });

  const handleEventSelect = (eventId: string) => {
    const params = new URLSearchParams(searchParams);
    params.set(EVENT_ID_PARAM, eventId);
    setSearchParams(params);
  };

  const selectedEventId = useMemo(() => searchParams.get(EVENT_ID_PARAM), [searchParams]);

  if (!isFetched) return <Loader />;

  const events = data?.events.map((event) => getFragmentData(syslogEventFragmentDocument, event)) || [];

  return (
    <Stack spacing={2}>
      <SystemLogTable
        events={events}
        isLoading={isFetching}
        onEventSelect={handleEventSelect}
        selectedEventId={selectedEventId}
      />
      {data && (
        <TablePagination
          pagination={{
            total: data.totalCount,
            limit: pageSize,
            offset,
          }}
          isLoading={isFetching}
          onChange={handlePageChange}
        />
      )}
    </Stack>
  );
}
